import { useState, useEffect } from 'react';
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButtons,
    IonBackButton,
    IonButton,
    IonIcon,
    IonCard,
    IonCardContent,
    IonBadge,
    IonToast,
    IonSearchbar,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonSpinner
} from '@ionic/react';
import {
    listOutline,
    gridOutline,
    checkmarkCircleOutline,
    closeCircleOutline,
    businessOutline,
    peopleOutline,
    mailOutline,
    callOutline,
    timeOutline,
    refreshOutline
} from 'ionicons/icons';
import axios from 'axios';
import API_CONFIG from '../config/api';
import './View.css';
import Loader from '../components/Loader';

const AdminOrganizations = () => {
    const [organizations, setOrganizations] = useState([]);
    const [stats, setStats] = useState({ total: 0, pending: 0, active: 0 });
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'active'
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', color: 'success' });

    useEffect(() => {
        fetchOrganizations();
    }, [filterStatus]);

    const fetchOrganizations = async () => {
        setLoading(true);
        const safetyTimeout = setTimeout(() => {
            setLoading(false);
        }, 12000);

        try {
            const userId = localStorage.getItem('userId');
            let url = `${API_CONFIG.MANAGE_ORGANIZATIONS}?user_id=${userId}`;
            if (filterStatus !== 'all') {
                url += `&status=${filterStatus}`;
            }
            const response = await axios.get(url, { timeout: 8000 });
            if (response.data.success) {
                setOrganizations(response.data.organizations);
                setStats(response.data.stats);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
            setToast({ show: true, message: 'Failed to load organizations', color: 'danger' });
        } finally {
            clearTimeout(safetyTimeout);
            setLoading(false);
        }
    };

    const handleAction = async (orgId, action) => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.put(`${API_CONFIG.MANAGE_ORGANIZATIONS}?user_id=${userId}`, {
                id: orgId,
                action: action
            });
            if (response.data.success) {
                setToast({ show: true, message: response.data.message, color: 'success' });
                fetchOrganizations();
            }
        } catch (error) {
            console.error('Error updating organization:', error);
            setToast({ show: true, message: 'Action failed', color: 'danger' });
        }
    };

    const filteredOrganizations = organizations.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (org.contact_email && org.contact_email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const pendingOrgs = filteredOrganizations.filter(org => !org.is_active);
    const activeOrgs = filteredOrganizations.filter(org => org.is_active);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const renderOrganizationCard = (org) => (
        <IonCard key={org.id} className="m-0 shadow-md hover:shadow-lg transition-all">
            <IonCardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                            {org.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 text-lg">{org.name}</h3>
                            <p className="text-sm text-gray-500">{org.organization_type || 'Organization'}</p>
                        </div>
                    </div>
                    <IonBadge color={org.is_active ? 'success' : 'warning'}>
                        {org.is_active ? 'Active' : 'Pending'}
                    </IonBadge>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                    {org.contact_email && (
                        <div className="flex items-center gap-2">
                            <IonIcon icon={mailOutline} className="text-gray-400" />
                            <span>{org.contact_email}</span>
                        </div>
                    )}
                    {org.contact_phone && (
                        <div className="flex items-center gap-2">
                            <IonIcon icon={callOutline} className="text-gray-400" />
                            <span>{org.contact_phone}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <IonIcon icon={peopleOutline} className="text-gray-400" />
                        <span>{org.member_count || 0} members</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <IonIcon icon={timeOutline} className="text-gray-400" />
                        <span>Registered {formatDate(org.created_at)}</span>
                    </div>
                    {org.admin_name && (
                        <div className="text-xs text-gray-500 mt-2">
                            Admin: {org.admin_name} ({org.admin_email})
                        </div>
                    )}
                </div>

                <div className="flex gap-2">
                    {!org.is_active ? (
                        <>
                            <IonButton
                                size="small"
                                color="success"
                                onClick={() => handleAction(org.id, 'approve')}
                                className="flex-1"
                            >
                                <IonIcon icon={checkmarkCircleOutline} slot="start" />
                                Approve
                            </IonButton>
                            <IonButton
                                size="small"
                                color="danger"
                                fill="outline"
                                onClick={() => handleAction(org.id, 'reject')}
                                className="flex-1"
                            >
                                <IonIcon icon={closeCircleOutline} slot="start" />
                                Reject
                            </IonButton>
                        </>
                    ) : (
                        <IonButton
                            size="small"
                            color="warning"
                            fill="outline"
                            onClick={() => handleAction(org.id, 'suspend')}
                            className="flex-1"
                        >
                            Suspend
                        </IonButton>
                    )}
                </div>
            </IonCardContent>
        </IonCard>
    );

    const renderListView = () => (
        <div className="space-y-4">
            {filteredOrganizations.map(org => renderOrganizationCard(org))}
        </div>
    );

    const renderKanbanView = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pending Column */}
            <div className="bg-amber-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                    <h3 className="font-bold text-gray-800">Pending Approval</h3>
                    <IonBadge color="warning">{pendingOrgs.length}</IonBadge>
                </div>
                <div className="space-y-4">
                    {pendingOrgs.length > 0 ? (
                        pendingOrgs.map(org => renderOrganizationCard(org))
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <IonIcon icon={checkmarkCircleOutline} className="text-4xl text-green-500 mb-2" />
                            <p>No pending organizations</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Active Column */}
            <div className="bg-green-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <h3 className="font-bold text-gray-800">Active Organizations</h3>
                    <IonBadge color="success">{activeOrgs.length}</IonBadge>
                </div>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                    {activeOrgs.length > 0 ? (
                        activeOrgs.map(org => renderOrganizationCard(org))
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <IonIcon icon={businessOutline} className="text-4xl text-gray-400 mb-2" />
                            <p>No active organizations</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <IonPage className="view-page admin-amber-theme">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar frosted-header">
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="/super-admin" text="Back" className="custom-back-button" />
                    </IonButtons>
                    <IonTitle>
                        <span className="title-text" style={{ background: 'none', WebkitTextFillColor: '#f59e0b', color: '#f59e0b' }}>Organization Management</span>
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={fetchOrganizations}>
                            <IonIcon icon={refreshOutline} slot="icon-only" />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                {loading && <Loader message="Verifying Organization Entities..." />}
                <div className="max-w-6xl mx-auto p-4 space-y-6">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white rounded-xl p-4 shadow-sm border" style={{ borderColor: '#f59e0b20' }}>
                            <p className="text-2xl font-bold" style={{ color: '#f59e0b' }}>{stats.total}</p>
                            <p className="text-sm text-gray-500">Total Organizations</p>
                        </div>
                        <div className="bg-amber-50 rounded-xl p-4 shadow-sm border border-amber-100">
                            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                            <p className="text-sm text-amber-700">Pending Approval</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-100">
                            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                            <p className="text-sm text-green-700">Active</p>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        <IonSearchbar
                            value={searchTerm}
                            onIonInput={(e) => setSearchTerm(e.detail.value)}
                            placeholder="Search organizations..."
                            className="max-w-md"
                        />

                        <div className="flex items-center gap-4">
                            {/* Filter Segment */}
                            <IonSegment 
                                value={filterStatus} 
                                onIonChange={(e) => setFilterStatus(e.detail.value)}
                                className="custom-segment-amber"
                                style={{ '--color-checked': '#f59e0b', '--indicator-color': '#f59e0b', '--indicator-color-checked': '#f59e0b' }}
                            >
                                <IonSegmentButton value="all">
                                    <IonLabel>All</IonLabel>
                                </IonSegmentButton>
                                <IonSegmentButton value="pending">
                                    <IonLabel>Pending</IonLabel>
                                </IonSegmentButton>
                                <IonSegmentButton value="active">
                                    <IonLabel>Active</IonLabel>
                                </IonSegmentButton>
                            </IonSegment>

                            {/* View Toggle */}
                            <div className="flex bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-green-600' : 'text-gray-500'}`}
                                >
                                    <IonIcon icon={listOutline} className="text-xl" />
                                </button>
                                <button
                                    onClick={() => setViewMode('kanban')}
                                    className={`p-2 rounded-lg transition-all ${viewMode === 'kanban' ? 'bg-white shadow-sm text-green-600' : 'text-gray-500'}`}
                                >
                                    <IonIcon icon={gridOutline} className="text-xl" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    {filteredOrganizations.length === 0 ? (
                        <div className="text-center py-20">
                            <IonIcon icon={businessOutline} className="text-6xl text-gray-300 mb-4" />
                            <h3 className="text-xl font-bold text-gray-600">No organizations found</h3>
                            <p className="text-gray-500">Organizations will appear here once registered</p>
                        </div>
                    ) : viewMode === 'list' ? (
                        renderListView()
                    ) : (
                        renderKanbanView()
                    )}
                </div>

                <IonToast
                    isOpen={toast.show}
                    onDidDismiss={() => setToast({ ...toast, show: false })}
                    message={toast.message}
                    duration={3000}
                    color={toast.color}
                    position="top"
                />
            </IonContent>
        </IonPage>
    );
};

export default AdminOrganizations;
